import { create } from 'zustand';

/**
 * THE BOARD SELECTION — the set of cards a person has ticked, held apart from any
 * one view so every surface that lists cards can drive it (project-bulk-card-changes).
 *
 * It is deliberately VIEW-AGNOSTIC: it knows card ids and the order they are laid
 * out in, nothing about columns, lanes, boards or the Board component. The Board
 * feeds it the flat visual order (for Shift-range), toggles ids, and clears; the
 * Grid view (#508) will mount the same store and the same BulkBar the moment it
 * exists, and get multi-select for free. Keeping the state here — not in Board.tsx
 * — is what makes that reuse possible without a rewrite.
 *
 * SELECTION IS AN EXPLICIT MODE. The board is in one of two states, held here as
 * `selectMode`: OFF (the default — no checkboxes, a click OPENS a card as it always
 * has) or ON (a Select toggle on the toolbar, or the `S` key, turns it on — every
 * card shows a checkbox and a click anywhere on a card ticks it, never opening).
 * The mode is BOARD-LEVEL state, not per-card hover, so a deselected card keeps its
 * box while you are still selecting, and leaving the mode (Esc, the toggle, the
 * bar's ✕, or a click on empty board space) clears the selection so NOTHING lingers.
 */

/**
 * The contiguous run of ids between two anchors in the laid-out order, inclusive
 * and direction-agnostic — Shift-click's range. Pure, so it is unit-tested without
 * a board: a Shift-click from the 5th card up to the 2nd selects 2,3,4,5 exactly as
 * 2→5 does. An id not in the order contributes only the one that is (a range to a
 * card that has since scrolled out of the set is still a sensible single pick).
 */
export function rangeBetween(order: string[], a: string, b: string): string[] {
  const i = order.indexOf(a);
  const j = order.indexOf(b);
  if (i < 0 && j < 0) return [];
  if (i < 0) return [b];
  if (j < 0) return [a];
  const [lo, hi] = i <= j ? [i, j] : [j, i];
  return order.slice(lo, hi + 1);
}

interface SelectionStore {
  /** Whether the board is in selection mode — the explicit on/off the Select toggle
   *  (and the `S` key) drives. NOT derived from `ids.size`: a card keeps its box
   *  while you deselect, and the mode is what shows the checkboxes at all. */
  selectMode: boolean;
  /** The ticked card ids. A fresh Set on every change, so a `s.ids.has(id)` selector
   *  re-renders only the card whose membership actually flipped. */
  ids: Set<string>;
  /** The last card toggled, from which a Shift-range is measured. */
  anchor: string | null;
  /** The flat visual order of the cards on screen, set by the view — Shift-range only. */
  order: string[];

  /** Turn selection mode ON — the toolbar toggle / the `S` key. */
  enterSelect: () => void;
  /** Turn selection mode OFF and clear everything, so nothing lingers — Esc, the
   *  toggle again, the bar's ✕, or a click on empty board space. */
  exitSelect: () => void;
  /** Flip the mode (the toolbar toggle / `S`): on → off (clearing), off → on. */
  toggleMode: () => void;
  /** The view tells the store how its cards are laid out, so a range can be computed. */
  setOrder: (order: string[]) => void;
  /** ⌘/Ctrl-click, the X key, or the checkbox — flip one card, and anchor the range here. */
  toggle: (id: string) => void;
  /** Shift-click — add the run from the anchor to here (union; the anchor holds, so a
   *  second Shift-click extends from the same start). With no anchor yet it is a toggle. */
  rangeTo: (id: string) => void;
  /** Add a set (a column's "select all"), leaving the rest of the selection untouched. */
  add: (ids: string[]) => void;
  /** Take a set out (a column's "select all" pressed again when all were on). */
  remove: (ids: string[]) => void;
  /** Keep exactly these selected. */
  only: (ids: string[]) => void;
  /** Esc, or after a batch — nothing selected. */
  clear: () => void;
}

export const useSelection = create<SelectionStore>((set, get) => ({
  selectMode: false,
  ids: new Set<string>(),
  anchor: null,
  order: [],

  enterSelect: () => set({ selectMode: true }),
  exitSelect: () => set({ selectMode: false, ids: new Set<string>(), anchor: null }),
  toggleMode: () =>
    get().selectMode
      ? set({ selectMode: false, ids: new Set<string>(), anchor: null })
      : set({ selectMode: true }),

  setOrder: (order) => set({ order }),

  toggle: (id) =>
    set((s) => {
      const ids = new Set(s.ids);
      if (ids.has(id)) ids.delete(id);
      else ids.add(id);
      return { ids, anchor: id };
    }),

  rangeTo: (id) => {
    const { anchor, order } = get();
    if (!anchor) {
      // No start point yet — a Shift-click behaves as a first pick, and sets the
      // anchor so the NEXT Shift-click has a range to draw.
      return set((s) => {
        const ids = new Set(s.ids);
        ids.add(id);
        return { ids, anchor: id };
      });
    }
    const run = rangeBetween(order, anchor, id);
    set((s) => {
      const ids = new Set(s.ids);
      for (const r of run) ids.add(r);
      return { ids }; // anchor deliberately holds, so the range can be re-extended
    });
  },

  add: (list) =>
    set((s) => {
      const ids = new Set(s.ids);
      for (const id of list) ids.add(id);
      return { ids, anchor: list[list.length - 1] ?? s.anchor };
    }),

  remove: (list) =>
    set((s) => {
      const ids = new Set(s.ids);
      for (const id of list) ids.delete(id);
      return { ids };
    }),

  only: (list) => set({ ids: new Set(list), anchor: list[list.length - 1] ?? null }),

  clear: () => set({ ids: new Set<string>(), anchor: null }),
}));
